import { createPortal } from "react-dom"

import { UndoRedoButton } from "@/tiptap/ui/undo-redo-button"
import { FindReplaceButton } from "@/tiptap/ui/find-replace-button"
import {
  MarkdownExportButton,
  MarkdownImportButton,
} from "@/tiptap/ui/markdown-io-button"
import {
  FormattingToolbarGroups,
  NotionToolbarFloating,
} from "@/tiptap/templates/notion-like/notion-like-editor-toolbar-floating"
import { MobileToolbar } from "@/tiptap/templates/notion-like/notion-like-editor-mobile-toolbar"

import { useIsBreakpoint } from "@/tiptap/hooks/use-is-breakpoint"
import { HUDU_ICON_BUTTON_CLASS } from "@/tiptap/lib/hudu-icon-button"
import { Button } from "@/tiptap/ui-primitive/button"
import { Spacer } from "@/tiptap/ui-primitive/spacer"
import { ButtonGroup } from "@/tiptap/ui-primitive/button-group"
import { Toolbar } from "@/tiptap/ui-primitive/toolbar"

const MOBILE_TOOLBAR_BREAKPOINT = 768

/**
 * Pinned formatting controls for users who prefer a fixed toolbar.
 * Small screens keep using the mobile toolbar instead.
 */
function FixedFormattingToolbar() {
  const isMobile = useIsBreakpoint("max", MOBILE_TOOLBAR_BREAKPOINT)

  if (isMobile) return null

  return (
    <Toolbar variant="header" aria-label="Formatting" className="notion-like-editor-header-formatting">
      <FormattingToolbarGroups hideWhenUnavailable={false} popoverSide="bottom" />
    </Toolbar>
  )
}

/**
 * Unified header toolbar that adapts to mode configuration.
 * In full mode, shows undo/redo, theme toggle, and source button.
 * In inline mode, renders nothing (or a minimal version).
 * With the fixed toolbar preference, formatting controls lead the header.
 */
export function EditorToolbar({ modeConfig, onToggleSource }) {
  const { toolbar, layout } = modeConfig

  if (!layout.showHeader) return null

  return (
    <header className="notion-like-editor-header">
      {layout.showFixedToolbar && <FixedFormattingToolbar />}
      <Spacer />
      <div className="notion-like-editor-header-actions">
        {toolbar.showUndoRedo && (
          <ButtonGroup>
            <ButtonGroup>
              <UndoRedoButton action="undo" />
            </ButtonGroup>
            <ButtonGroup>
              <UndoRedoButton action="redo" />
            </ButtonGroup>
          </ButtonGroup>
        )}

        {toolbar.showFindReplace && (
          <ButtonGroup>
            <FindReplaceButton />
          </ButtonGroup>
        )}

        {(toolbar.showMarkdownImport || toolbar.showMarkdownExport) && (
          <ButtonGroup>
            {toolbar.showMarkdownImport && <MarkdownImportButton />}
            {toolbar.showMarkdownExport && <MarkdownExportButton />}
          </ButtonGroup>
        )}

        {toolbar.showSourceMode && onToggleSource && (
          <ButtonGroup>
            <Button
              type="button"
              variant="ghost"
              role="button"
              tabIndex={-1}
              aria-label="Source code"
              tooltip="Source code"
              className={HUDU_ICON_BUTTON_CLASS}
              unstyled
              onClick={onToggleSource}
            >
              <span style={{ fontFamily: "monospace", fontSize: "0.8125rem", lineHeight: 1, whiteSpace: "nowrap" }}>{"</>"}</span>
            </Button>
          </ButtonGroup>
        )}
      </div>
    </header>
  )
}

/**
 * Renders floating and mobile toolbars based on mode config.
 * These are rendered as children of EditorContent so they have
 * access to the editor context.
 */
export function EditorOverlayToolbars({ modeConfig }) {
  return (
    <>
      {modeConfig.layout.showFloatingToolbar && <NotionToolbarFloating />}
      {modeConfig.layout.showMobileToolbar &&
        createPortal(<MobileToolbar />, document.body)}
    </>
  )
}

export default EditorToolbar
